import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukDayTime } from "@/lib/time";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Kv, Sw } from "@/components/portal/crm/kit";

/** Switch an introducer's API on or off, and see whether they've wired it up. */
export const ApiAccessDialog = ({ party, onOpenChange, onChanged }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const id = party?.id;

  useEffect(() => {
    if (!id) { setD(null); return; }
    api.get(`/hub/referrers/${id}/api-access`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [id]);

  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch(`/hub/referrers/${id}/api-access`, { enabled: !d.enabled });
      setD((x) => ({ ...x, enabled: !x.enabled }));
      toast.success(!d.enabled ? `API on — ${party.name} now sees the API tab` : "API off — their key stops working straight away");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't go through"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!party} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="hub-api-access-dialog">
        <DialogHeader>
          <DialogTitle>API access · {party?.company || party?.name}</DialogTitle>
          <DialogDescription>
            Their own system sends cases straight in. API cases are never auto-routed — they wait at New for you to place.
          </DialogDescription>
        </DialogHeader>
        {!d ? <div className="text-sm text-slate-500" data-testid="hub-api-access-loading">Reading…</div> : (
          <div className="cxp" style={{ display: "grid", gap: 16, background: "transparent" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 14, fontWeight: 600 }}>
              <Sw on={d.enabled} onClick={toggle} disabled={busy || (d.campaign && !d.enabled)} testId="hub-api-access-toggle" label="API access" />
              <span data-testid="hub-api-access-state">{d.enabled ? "API is on" : "API is off"}</span>
            </label>
            {d.campaign && <p style={{ margin: 0, fontSize: 12.5, color: "var(--mut)" }}>Campaign partners send applications through their links, not the API.</p>}
            <div data-testid="hub-api-access-meta">
              <Kv rows={[
                ["Key", d.key ? `${d.key.prefix} · ${d.key.last_used_at ? `used ${ukDayTime(d.key.last_used_at)}` : "not used yet"}` : "Not created yet"],
                ["Stage updates", d.webhook?.enabled ? d.webhook.url : "Off"],
                ["Last update", d.webhook?.last_at ? `${d.webhook.last_status || "no answer"} · ${ukDayTime(d.webhook.last_at)}` : "—"],
                ["Cases by API", `${d.cases}${d.duplicates ? ` · ${d.duplicates} flagged as possible duplicates` : ""}`],
              ]} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
